// src/pages/Vacatures.js
// vVAC-020
// - Opdracht gekoppeld aan Vacatures; defaults worden alleen in lege velden ingevuld
// - Opdracht toegevoegd boven Relatie en in grid tussen Status en CAO
// vVAC-019
// - Bezoekadres relatie wordt nu ook bij Aanvraag -> Vacature gevuld als organisaties nog niet geladen zijn
// - Rubriek Plaats verwijderd uit rechterkolom; Regio blijft voor matching
// vVAC-018
// - Locatie/bezoekadres uitgebreid; bezoekadres relatie automatisch ingevuld wanneer vorige stap leeg is
// - Contact op locatie en Aanmeldprocedure toegevoegd en opgenomen in vacaturedocument
// - Opzegtermijn toegevoegd onder Einddatum, overgenomen vanuit Aanvragen en opgenomen in vacaturedocument
// vVAC-016
// - Reisuren label gewijzigd naar Reisuren per dag
// - Reiskosten uitgebreid met keuze per km / per dag en overgenomen vanuit Aanvragen
// vVAC-015
// - Reisuren toegevoegd en overgenomen vanuit Aanvragen
// vVAC-014
// - Labels gelijkgetrokken met Aanvragen: Vacaturenummer -> Vacaturereferentie; Vacature -> Korte omschrijving
// vVAC-013
// - Bij overname vanuit Aanvragen blijft een duidelijke melding staan tot de Vacature is opgeslagen
// - Kan rechtstreeks vanuit Aanvragen worden geopend en neemt de aanvraag automatisch over
// - Matching Personeel ontvangt nu vacature-id én aanvraag-id zodat de plaatsing gekoppeld blijft
// vVAC-011
//
// StaffIQ HRM - Vacatures
// Opzet gelijk aan Personeel / Matching:
// - Links/boven: zoeken + knoppen
// - Midden: overzicht met sortering en kolomfilters
// - Rechts: detailkaart

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vVAC-020";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function parseDecimal(v) {
  const raw = String(v ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return 0;

  let cleaned = raw;

  if (cleaned.includes(",")) {
    // Nederlandse invoer: 2,35 of 2.350,75
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // Engelse/database-notatie: 2.35 moet 2.35 blijven.
    // Alleen meerdere punten zien we als duizendtallen: 2.350.750 -> 2350750
    const dotCount = (cleaned.match(/\./g) || []).length;
    if (dotCount > 1) cleaned = cleaned.replace(/\./g, "");
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function decimalInput(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";

  const n = parseDecimal(v);
  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function cleanDecimalInputValue(v) {
  return String(v || "").replace(/[^0-9,.-]/g, "");
}

function formatDecimalInputValue(v, decimals = 2) {
  const raw = String(v || "").trim();
  if (!raw) return "";
  const n = parseDecimal(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function boolToJaNee(v) {
  return v === true ? "j" : "n";
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let role = "user";
  try {
    role =
      JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      )?.role || "user";
  } catch {}

  let lastErr = null;

  for (const url of urls) {
    try {
      let user = {};
      try {
        user = JSON.parse(
          sessionStorage.getItem("user") ||
          localStorage.getItem("user") ||
          "{}"
        ) || {};
      } catch {}

      const companyCodeIds =
        sessionStorage.getItem("staffiq_company_code_ids") ||
        localStorage.getItem("staffiq_company_code_ids") ||
        sessionStorage.getItem("company_code_ids") ||
        localStorage.getItem("company_code_ids") ||
        "";

      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-staffiq-user-id": user?.id || user?.user_id || "",
        "x-staffiq-user-email": user?.email || "",
        "x-staffiq-user-name": user?.display_name || user?.name || "",
        "x-company-code-ids": companyCodeIds,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function emptyFormToday() {
  return {
    id: null,
    application_id: "",
    order_id: "",
    vacancy_date: new Date().toISOString().slice(0, 10),
    status: "Open",
    vacancy_number: "",
    organisation_id: "",
    contact_id: "",
    company_code_id: "",
    title: "",
    job_title: "",
    number_requested: "",
    number_proposed: "",
    number_placed: "",
    internal_description: "",
    external_text: "",
    work_description: "",
    requirements_text: "",
    offer_text: "",
    start_date: "",
    end_date: "",
    notice_period: "",
    hours_per_week: "",
    plan_weekends: false,
    schedule_text: "",
    location: "",
    location_contact: "",
    registration_procedure: "",
    place: "",
    region: "",
    education: "",
    experience: "",
    certificates: "",
    driving_license: "",
    own_transport: "",
    company_transport: false,
    employment_vast: true,
    employment_temporary: true,
    employment_zzp: true,
    employment_doorleen: true,
    languages: "",
    matching_text: "",
    cao: "",
    wage_period: "week",
    invoice_period: "week",
    factor: "",
    rate_indication: "",
    sales_rate: "",
    min_rate: "",
    travel_costs: "",
    travel_costs_unit: "km",
    travel_hours_amount: "",
    allowances: "",
    invoice_agreement: "",
    priority: "Normaal",
    request_source: "",
    proposal_deadline: "",
    internal_note: "",
    external_note: "",
    remarks: "",
    active: true,
  };
}


function organisationVisitAddressText(org = {}) {
  const name = safeTrim(org?.name || org?.naam || org?.bedrijfsnaam || org?.search_name || org?.zoeknaam || "");
  const street = safeTrim(org?.visit_street || org?.bezoek_straat || "");
  const houseNumber = safeTrim(org?.visit_house_number || org?.bezoek_huisnummer || "");
  const addition = safeTrim(org?.visit_addition || org?.bezoek_toevoeging || "");
  const postcode = safeTrim(org?.visit_postcode || org?.bezoek_postcode || "");
  const city = safeTrim(org?.visit_city || org?.bezoek_plaats || "");
  const country = safeTrim(org?.visit_country || org?.bezoek_land || "");
  const streetLine = [street, houseNumber, addition].filter(Boolean).join(" ");
  const cityLine = [postcode, city].filter(Boolean).join(" ");
  return [name, streetLine, cityLine, country].filter(Boolean).join("\n");
}

function organisationVisitPlace(org = {}) {
  return safeTrim(org?.visit_city || org?.bezoek_plaats || "");
}

function organisationVisitRegion(org = {}) {
  return safeTrim(org?.visit_region || org?.region || org?.regio || "");
}

function pickOrgName(o) {
  return safeTrim(o?.search_name || o?.zoeknaam || o?.name || o?.naam || o?.bedrijfsnaam || "");
}

function orgIdOf(o) {
  return o?.id ?? o?.organisation_id ?? o?.organisatie_id ?? "";
}

function orgNameFromList(list, id, fallback = "") {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return pickOrgName(found) || fallback || "";
}

function pickCompanyCodeName(row = {}) {
  return safeTrim(row?.company_code_name || row?.company_code || row?.company_code_display || "");
}

function companyCodeNameFromOrganisation(list, id, fallback = "") {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return pickCompanyCodeName(found) || fallback || "";
}

function companyCodeIdFromOrganisation(list, id, fallback = "") {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return found?.company_code_id ?? fallback ?? "";
}

function pickVacancyCompanyCodeName(item = {}, organisations = []) {
  return pickCompanyCodeName(item) || companyCodeNameFromOrganisation(organisations, item?.organisation_id, "");
}

function pickVacancyCompanyCodeId(item = {}, organisations = []) {
  return item?.company_code_id ?? companyCodeIdFromOrganisation(organisations, item?.organisation_id, "");
}

function pickContactName(c) {
  const fn = safeTrim(c?.first_name || c?.voornaam || "");
  const prefix = safeTrim(c?.name_prefix || c?.tussenvoegsel || c?.tussenvoegsels || "");
  const ln = safeTrim(c?.last_name || c?.achternaam || "");
  const full = safeTrim(c?.full_name || c?.naam || "");

  const naturalName = safeTrim([fn, prefix, ln].filter(Boolean).join(" "));

  return safeTrim(
    naturalName ||
    full ||
    c?.display_name ||
    c?.search_name ||
    fn ||
    ""
  );
}

function contactIdOf(c) {
  return c?.id ?? c?.contact_id ?? "";
}

function normalizeVacancy(raw = {}) {
  return {
    ...raw,
    id: raw.id ?? null,
    application_id: raw.application_id ?? "",
    order_id: raw.order_id ?? raw.hrm_order_id ?? "",
    vacancy_date: isoDate(raw.vacancy_date),
    start_date: isoDate(raw.start_date),
    end_date: isoDate(raw.end_date),
    notice_period: raw.notice_period ?? "",
    location_contact: raw.location_contact ?? "",
    registration_procedure: raw.registration_procedure ?? "",
    proposal_deadline: isoDate(raw.proposal_deadline),
    organisation_id: raw.organisation_id ?? "",
    contact_id: raw.contact_id ?? "",
    company_code_id: raw.company_code_id ?? "",
    company_code: raw.company_code ?? "",
    company_code_name: raw.company_code_name ?? "",
    number_requested: raw.number_requested ?? "",
    number_proposed: raw.number_proposed ?? "",
    number_placed: raw.number_placed ?? "",
    hours_per_week: decimalInput(raw.hours_per_week, 2),
    factor: decimalInput(raw.factor, 2),
    rate_indication: decimalInput(raw.rate_indication, 2),
    sales_rate: decimalInput(raw.sales_rate, 2),
    min_rate: decimalInput(raw.min_rate ?? raw.max_rate, 2),
    travel_costs: decimalInput(raw.travel_costs, 2),
    travel_costs_unit: raw.travel_costs_unit === "day" ? "day" : "km",
    travel_hours_amount: decimalInput(raw.travel_hours_amount, 2),
    plan_weekends: raw.plan_weekends === true,
    company_transport: raw.company_transport === true,
    employment_vast: raw.employment_vast !== false,
    employment_temporary: raw.employment_temporary !== false,
    employment_zzp: raw.employment_zzp !== false,
    employment_doorleen: raw.employment_doorleen !== false,
    active: parseActiveValue(raw) !== false,
  };
}

export default function Vacatures() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";

  const [items, setItems] = useState([]);
  const [applications, setApplications] = useState([]);
  const [orders, setOrders] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyFormToday());
  const [listIndex, setListIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");
  const [applicationReadyMessage, setApplicationReadyMessage] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");
  const [documentSettings, setDocumentSettings] = useState(null);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [q, setQ] = useState(getParam("q"));
  const [organisationFilterId, setOrganisationFilterId] = useState(getParam("organisation_id"));

  const EMPTY_VAL = "__EMPTY__";
  const [dateFilter, setDateFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderFilter, setOrderFilter] = useState("");
  const [caoFilter, setCaoFilter] = useState("");
  const [wagePeriodFilter, setWagePeriodFilter] = useState("");
  const [invoicePeriodFilter, setInvoicePeriodFilter] = useState("");
  const [factorFilter, setFactorFilter] = useState("");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");

  const [mode, setMode] = useState("search");
  const isFormLocked = isReadonlyRole || mode !== "form";
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  const pageTopRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const reopenHandledRef = useRef(false);
  const createFromApplicationHandledRef = useRef(false);
  const rightScrollRef = useRef(null);

  const dateRef = useRef(null);
  const numberRef = useRef(null);
  const applicationRef = useRef(null);
  const orderRef = useRef(null);
  const relationRef = useRef(null);
  const contactRef = useRef(null);
  const titleRef = useRef(null);
  const functionRef = useRef(null);
  const statusRef = useRef(null);
  const peopleRef = useRef(null);
  const workDescriptionRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const noticePeriodRef = useRef(null);
  const hoursRef = useRef(null);
  const scheduleRef = useRef(null);
  const locationRef = useRef(null);
  const locationContactRef = useRef(null);
  const registrationProcedureRef = useRef(null);
  const regionRef = useRef(null);
  const educationRef = useRef(null);
  const experienceRef = useRef(null);
  const certificatesRef = useRef(null);
  const drivingLicenseRef = useRef(null);
  const ownTransportRef = useRef(null);
  const languagesRef = useRef(null);
  const matchingTextRef = useRef(null);
  const caoRef = useRef(null);
  const wagePeriodRef = useRef(null);
  const invoicePeriodRef = useRef(null);
  const factorRef = useRef(null);
  const rateIndicationRef = useRef(null);
  const salesRateRef = useRef(null);
  const minRateRef = useRef(null);
  const travelCostsRef = useRef(null);
  const travelCostsUnitRef = useRef(null);
  const travelHoursAmountRef = useRef(null);
  const allowancesRef = useRef(null);
  const invoiceAgreementRef = useRef(null);
  const priorityRef = useRef(null);
  const sourceRef = useRef(null);
  const proposalDeadlineRef = useRef(null);
  const internalNoteRef = useRef(null);
  const externalNoteRef = useRef(null);

  const focusOrderRefs = [
    dateRef,
    numberRef,
    applicationRef,
    orderRef,
    relationRef,
    contactRef,
    titleRef,
    functionRef,
    statusRef,
    peopleRef,
    workDescriptionRef,
    startDateRef,
    endDateRef,
    noticePeriodRef,
    hoursRef,
    scheduleRef,
    locationRef,
    locationContactRef,
    registrationProcedureRef,
    regionRef,
    educationRef,
    experienceRef,
    certificatesRef,
    drivingLicenseRef,
    ownTransportRef,
    languagesRef,
    matchingTextRef,
    caoRef,
    wagePeriodRef,
    invoicePeriodRef,
    factorRef,
    rateIndicationRef,
    salesRateRef,
    minRateRef,
    travelCostsRef,
    travelCostsUnitRef,
    travelHoursAmountRef,
    allowancesRef,
    invoiceAgreementRef,
    priorityRef,
    sourceRef,
    proposalDeadlineRef,
    internalNoteRef,
    externalNoteRef,
  ];


  async function loadVacancyDocumentSettings(companyCodeId = form.company_code_id) {
    const id = Number(companyCodeId || 0);
    const suffix = id > 0 ? `?company_code_id=${encodeURIComponent(id)}` : "";
    const data = await tryFetchJson([`/api/company-section-settings/hrm_vacancies${suffix}`]);
    const s = data?.settings || {};
    setDocumentSettings(s);
    return s;
  }

  function esc(v) {
    return String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
  }

  function vacancyDocumentHtml(s = documentSettings || {}) {
    const org=(organisations||[]).find(o=>String(orgIdOf(o))===String(form.organisation_id||""));
    const con=(contacts||[]).find(c=>String(contactIdOf(c))===String(form.contact_id||""));
    const rows=[]; const add=(show,label,value)=>{const v=String(value??"").trim();if(show===true&&v)rows.push(`<tr><td class="l">${esc(label)}</td><td>${esc(v).replace(/\n/g,"<br>")}</td></tr>`)};
    add(s.show_relation,"Relatie",pickOrgName(org)); add(s.show_contact,"Contactpersoon",pickContactName(con)); add(s.show_function,"Functie",form.job_title); add(s.show_vacancy_title,"Korte omschrijving",form.title); add(s.show_number_requested,"Aantal gevraagd",form.number_requested);
    if(s.show_period===true){add(true,"Startdatum",formatDateNL(form.start_date));add(true,"Einddatum",formatDateNL(form.end_date))}
    add(s.show_notice_period !== false,"Opzegtermijn",form.notice_period);
    add(s.show_hours_per_week,"Uren per week",form.hours_per_week); add(s.show_weekends,"Doorplannen in weekend",form.plan_weekends?"Ja":"Nee"); add(s.show_schedule,"Werktijden / rooster",form.schedule_text);
    if(s.show_location===true)add(true,"Locatie / bezoekadres",form.location);
    add(s.show_location_contact===true,"Contact op locatie",form.location_contact);
    add(s.show_registration_procedure===true,"Aanmeldprocedure",form.registration_procedure);
    add(s.show_priority,"Prioriteit",form.priority);
    add(s.show_work_description,"Werkzaamheden",form.work_description); add(s.show_requirements,"Functie-eisen",form.requirements_text); add(s.show_offer,"Aanbod / voorwaarden",form.offer_text); add(s.show_education,"Opleiding",form.education); add(s.show_experience,"Werkervaring",form.experience); add(s.show_certificates,"Certificaten",form.certificates); add(s.show_driving_license,"Rijbewijs",form.driving_license);
    if(s.show_transport===true)add(true,"Vervoer",[form.own_transport?`Eigen vervoer: ${form.own_transport}`:"",form.company_transport?"Vervoer van de zaak mogelijk":""].filter(Boolean).join(" / "));
    if(s.show_employment_types===true)add(true,"Toegestane inzetvorm",[form.employment_vast?"Vast":"",form.employment_temporary?"Tijdelijk":"",form.employment_zzp?"ZZP":"",form.employment_doorleen?"Doorleen":""].filter(Boolean).join(", "));
    add(s.show_languages,"Talen",form.languages); add(s.show_cao,"CAO",form.cao); add(s.show_rate_indication,"Salarisindicatie",form.rate_indication); add(s.show_external_text,"Vacaturetekst",form.external_text); add(s.show_external_note,"Opmerking",form.external_note);
    add(s.show_factor,"Factor",form.factor); add(s.show_min_rate,"Minimaal tarief",form.min_rate); add(s.show_invoice_agreement,"Factuurafspraak",form.invoice_agreement); add(s.show_internal_description,"Interne omschrijving",form.internal_description); add(s.show_internal_note,"Interne opmerking",form.internal_note);
    const addr=[s.company_address_1,s.company_address_2,[s.company_postcode,s.company_city].filter(Boolean).join(" "),s.company_country].filter(Boolean).map(esc).join("<br>");
    return `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:Arial;color:#222;font-size:12px;margin:28px}.top{display:flex;justify-content:space-between;gap:30px;margin-bottom:25px}.logo{max-width:190px;max-height:85px}.right{text-align:right}h1{font-size:22px}.intro,.footer{margin:18px 0}table{width:100%;border-collapse:collapse}.l{width:190px;font-weight:700;vertical-align:top}td{padding:6px 8px;border-bottom:1px solid #e8e8e8}.footer{margin-top:28px;padding-top:10px;border-top:1px solid #ddd;color:#666;font-size:10px}</style></head><body><div class="top"><div>${s.logo_url?`<img class="logo" src="${s.logo_url}">`:""}<div><b>${esc(s.company_name||"")}</b><br>${addr}</div></div><div class="right"><h1>${esc(s.document_title||"Vacature")}</h1><div>${esc(form.vacancy_number||"")}</div></div></div>${s.intro_text?`<div class="intro">${s.intro_text}</div>`:""}<table>${rows.join("")}</table>${s.footer_text?`<div class="footer">${s.footer_text}</div>`:""}</body></html>`;
  }

  function currentVacancySenderName(settings = {}) {
    let user = {};
    try {
      user = JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      ) || {};
    } catch {}

    return String(
      user?.display_name ||
      user?.name ||
      settings?.company_name ||
      "StaffIQ"
    ).trim() || "StaffIQ";
  }

  function openVacancyPreviewWindow(html) {
    const w = window.open("", "_blank", "width=1050,height=800,resizable=yes,scrollbars=yes");
    if (!w) {
      alert("Popup geblokkeerd. Sta popups toe voor StaffIQ.");
      return false;
    }

    const toolbar = `
      <div id="staffiq-preview-toolbar" style="
        position:sticky;top:0;z-index:99999;
        display:flex;justify-content:flex-end;gap:8px;
        padding:10px 14px;background:#f4f6f8;border-bottom:1px solid #ccd6e0;
        font-family:Arial,Helvetica,sans-serif;
      ">
        <button onclick="window.print()" style="
          border:0;border-radius:5px;padding:8px 14px;
          background:#5f6f7f;color:white;cursor:pointer;font-weight:600;
        ">🖨 Afdrukken</button>
        <button onclick="window.close()" style="
          border:0;border-radius:5px;padding:8px 14px;
          background:#1f6fb2;color:white;cursor:pointer;font-weight:600;
        ">Sluiten</button>
      </div>
    `;

    const withToolbar = String(html || "").replace(
      /<body([^>]*)>/i,
      `<body$1>${toolbar}`
    );

    w.document.open();
    w.document.write(withToolbar);
    w.document.close();
    return true;
  }

  async function vacancyDocumentAction(kind) {
    if(!form.id)return alert("Sla de vacature eerst op.");
    try{
      setDocumentBusy(true); const s=documentSettings||await loadVacancyDocumentSettings(form.company_code_id); const html=vacancyDocumentHtml(s);
      if(kind==="preview"){openVacancyPreviewWindow(html);return}
      if(kind==="save")await tryFetchJson([`/api/vacancies/${form.id}/save-document-html`],{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({html})});
      if(kind==="email"){
        const mailResult = await tryFetchJson([`/api/vacancies/${form.id}/send-email`],{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({html})
        });
        const accepted = Array.isArray(mailResult?.accepted) ? mailResult.accepted.filter(Boolean) : [];
        const recipient = accepted[0] || "het e-mailadres van de contactpersoon/relatie";
        alert(
          `Vacature per e-mail verzonden naar ${recipient}.\n\n` +
          (accepted.length
            ? `Geaccepteerd door mailserver: ${accepted.join(", ")}`
            : "De mailserver heeft het bericht verwerkt.")
        );
      }
      if(kind==="whatsapp"){
        const con=(contacts||[]).find(c=>String(contactIdOf(c))===String(form.contact_id||""));const org=(organisations||[]).find(o=>String(orgIdOf(o))===String(form.organisation_id||""));const phone=String(con?.mobile||con?.phone||org?.mobile||org?.phone||"").trim();if(!phone)return alert("Geen mobiel nummer gevonden.");
        const senderName=currentVacancySenderName(s);
        const senderEmail=String(s?.company_email||"").trim();
        let msg=String(s.whatsapp_text||"Hierbij ontvangt u vacature {document_nummer} voor {relatie}.")
          .replaceAll("{document_nummer}",String(form.vacancy_number||form.id||""))
          .replaceAll("{relatie}",pickOrgName(org))
          .replaceAll("{contactpersoon}",pickContactName(con))
          .replaceAll("{functie}",String(form.job_title||form.title||""))
          .replaceAll("{verzender_naam}",senderName)
          .replaceAll("{verzender_email}",senderEmail);
        let nr=phone.replace(/[^\d+]/g,"").replace(/^00/,"+");if(nr.startsWith("0"))nr="31"+nr.slice(1);nr=nr.replace("+","");window.open(`https://wa.me/${nr}?text=${encodeURIComponent(msg)}`,"_blank");
      }
      setFlash(kind==="save"?"Vacaturedocument opgeslagen ✅":"");setTimeout(()=>setFlash(""),1400);
    }catch(e){alert(e?.message||"Documentactie mislukt.")}finally{setDocumentBusy(false)}
  }

  async function loadOrders() {
    try {
      const data = await tryFetchJson(["/api/hrm-orders"]);
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    }
  }

  function orderReference(item = {}) {
    const wanted = String(item?.order_id ?? item?.hrm_order_id ?? "");
    const found = (orders || []).find((o) => String(o?.id ?? "") === wanted);
    return safeTrim(found?.order_reference || item?.order_reference || "");
  }

  function applyOrderDefaults(orderId) {
    const selectedOrder = (orders || []).find((o) => String(o?.id ?? "") === String(orderId || ""));
    setForm((prev) => {
      if (!selectedOrder) return { ...prev, order_id: orderId || "" };
      const organisationId = selectedOrder.organisation_id ? String(selectedOrder.organisation_id) : "";
      const orderOrg = (organisations || []).find((o) => String(orgIdOf(o)) === organisationId);
      return {
        ...prev,
        order_id: String(selectedOrder.id || ""),
        organisation_id: safeTrim(prev.organisation_id) || organisationId,
        contact_id: safeTrim(prev.contact_id) || (selectedOrder.contact_id ? String(selectedOrder.contact_id) : ""),
        company_code_id: safeTrim(prev.company_code_id) || (selectedOrder.company_code_id ? String(selectedOrder.company_code_id) : "") || companyCodeIdFromOrganisation(organisations, organisationId, ""),
        company_code_name: safeTrim(prev.company_code_name) || safeTrim(selectedOrder.company_code_name) || companyCodeNameFromOrganisation(organisations, organisationId, ""),
        location: safeTrim(prev.location) || safeTrim(selectedOrder.location) || organisationVisitAddressText(orderOrg),
        place: safeTrim(prev.place) || organisationVisitPlace(orderOrg),
        region: safeTrim(prev.region) || organisationVisitRegion(orderOrg),
        start_date: safeTrim(prev.start_date) || isoDate(selectedOrder.start_date),
        end_date: safeTrim(prev.end_date) || isoDate(selectedOrder.end_date),
        cao: safeTrim(prev.cao) || safeTrim(selectedOrder.cao),
      };
    });
  }

  async function loadOrganisations() {
    try {
      const data = await tryFetchJson(["/api/organisations"]);
      setOrganisations(Array.isArray(data) ? data : []);
    } catch {
      setOrganisations([]);
    }
  }

  async function loadApplications() {
    try {
      const data = await tryFetchJson(["/api/applications", "/api/aanvragen"]);
      setApplications(Array.isArray(data) ? data.map(normalizeApplicationForVacancySelect) : []);
    } catch {
      setApplications([]);
    }
  }

  function normalizeApplicationForVacancySelect(raw = {}) {
    return {
      ...raw,
      id: raw.id ?? null,
      application_date: isoDate(raw.application_date),
      start_date: isoDate(raw.start_date),
      end_date: isoDate(raw.end_date),
      notice_period: raw.notice_period ?? "",
      proposal_deadline: isoDate(raw.proposal_deadline),
    };
  }

  function applicationLabel(app) {
    const nr = safeTrim(app?.application_number || "");
    const rel = orgNameFromList(organisations, app?.organisation_id, app?.organisation_name || "");
    const title = safeTrim(app?.title || app?.job_title || "");
    return [nr, rel, title].filter(Boolean).join(" - ") || `Aanvraag #${app?.id}`;
  }

  async function applyApplicationToForm(applicationId = form.application_id) {
    const id = safeTrim(applicationId);
    if (!id) {
      alert("Kies eerst een aanvraag.");
      return;
    }

    try {
      const app = normalizeApplicationForVacancySelect(
        await tryFetchJson([`/api/applications/${id}`, `/api/aanvragen/${id}`])
      );

      let appOrg = (organisations || []).find(
        (o) => String(orgIdOf(o)) === String(app.organisation_id || "")
      );

      if (!appOrg && app.organisation_id) {
        try {
          appOrg = await tryFetchJson([
            `/api/organisations/${encodeURIComponent(app.organisation_id)}`,
          ]);
        } catch {
          appOrg = null;
        }
      }

      setForm((prev) => ({
        ...prev,
        application_id: app.id || "",
        order_id: app.order_id || prev.order_id || "",
        organisation_id: app.organisation_id || "",
        contact_id: app.contact_id || "",
        company_code_id: app.company_code_id ?? companyCodeIdFromOrganisation(organisations, app.organisation_id, ""),
        company_code: app.company_code || "",
        company_code_name: app.company_code_name || companyCodeNameFromOrganisation(organisations, app.organisation_id, ""),
        title: app.title || prev.title || "",
        job_title: app.job_title || prev.job_title || "",
        number_requested: app.number_of_people ?? prev.number_requested ?? "",
        work_description: app.work_description || prev.work_description || "",
        start_date: app.start_date || prev.start_date || "",
        end_date: app.end_date || prev.end_date || "",
        notice_period: app.notice_period || prev.notice_period || "",
        hours_per_week: decimalInput(app.hours_per_week, 2) || prev.hours_per_week || "",
        plan_weekends: app.plan_weekends === true,
        schedule_text: app.schedule_text || prev.schedule_text || "",
        location: app.location || prev.location || organisationVisitAddressText(appOrg),
        location_contact: app.location_contact || prev.location_contact || "",
        registration_procedure: app.registration_procedure || prev.registration_procedure || "",
        place: app.place || prev.place || organisationVisitPlace(appOrg),
        region: app.region || prev.region || organisationVisitRegion(appOrg),
        education: app.education || prev.education || "",
        experience: app.experience || prev.experience || "",
        certificates: app.certificates || prev.certificates || "",
        driving_license: app.driving_license || prev.driving_license || "",
        own_transport: app.own_transport || prev.own_transport || "",
        company_transport: app.company_transport === true,
        employment_vast: app.employment_vast !== false,
        employment_temporary: app.employment_temporary !== false,
        employment_zzp: app.employment_zzp !== false,
        employment_doorleen: app.employment_doorleen !== false,
        languages: app.languages || prev.languages || "",
        matching_text: app.matching_text || prev.matching_text || "",
        cao: app.cao || prev.cao || "",
        wage_period: app.wage_period || prev.wage_period || "week",
        invoice_period: app.invoice_period || prev.invoice_period || "week",
        factor: decimalInput(app.factor, 2) || prev.factor || "",
        rate_indication: decimalInput(app.rate_indication, 2) || prev.rate_indication || "",
        sales_rate: decimalInput(app.sales_rate, 2) || prev.sales_rate || "",
        min_rate: decimalInput(app.min_rate ?? app.max_rate, 2) || prev.min_rate || "",
        travel_costs: decimalInput(app.travel_costs, 2) || prev.travel_costs || "",
        travel_costs_unit: app.travel_costs_unit === "day" ? "day" : "km",
        travel_hours_amount: decimalInput(app.travel_hours_amount, 2) || prev.travel_hours_amount || "",
        allowances: app.allowances || prev.allowances || "",
        invoice_agreement: app.invoice_agreement || prev.invoice_agreement || "",
        priority: app.priority || prev.priority || "Normaal",
        request_source: app.request_source || prev.request_source || "",
        proposal_deadline: app.proposal_deadline || prev.proposal_deadline || "",
        internal_note: app.internal_note || prev.internal_note || "",
        external_note: app.external_note || prev.external_note || "",
      }));

      setApplicationReadyMessage("Aanvraag staat klaar. Vul de vacature aan en klik op Opslaan.");
    } catch (e) {
      alert(e?.message || "Aanvraag overnemen mislukt.");
    }
  }

  function openMatchingPersoneel() {
    const vacancyId = form.id || selectedId;

    if (!vacancyId) {
      alert("Sla de vacature eerst op voordat je personeel gaat matchen.");
      return;
    }

    const params = new URLSearchParams();
    params.set("vacancy_id", String(vacancyId));

    if (form.application_id) params.set("application_id", String(form.application_id));
    if (safeTrim(form.job_title || form.title)) params.set("functie", safeTrim(form.job_title || form.title));
    if (safeTrim(form.region)) params.set("regio", safeTrim(form.region));
    if (safeTrim(form.place)) params.set("plaats", safeTrim(form.place));
    if (safeTrim(form.driving_license)) params.set("rijbewijs", safeTrim(form.driving_license));
    if (safeTrim(form.experience)) params.set("ervaring", safeTrim(form.experience));
    if (safeTrim(form.own_transport)) params.set("eigenVervoer", safeTrim(form.own_transport));
    params.set("inzetVast", form.employment_vast !== false ? "1" : "0");
    params.set("inzetTijdelijk", form.employment_temporary !== false ? "1" : "0");
    params.set("inzetZZP", form.employment_zzp !== false ? "1" : "0");
    params.set("inzetDoorleen", form.employment_doorleen !== false ? "1" : "0");
    if (safeTrim(form.matching_text)) params.set("matchingGegevens", safeTrim(form.matching_text));

    const searchText = safeTrim(form.job_title || form.title || "");
    if (searchText) params.set("q", searchText);

    try {
      sessionStorage.setItem("staffiq_active_module", "HRM");
      localStorage.setItem("staffiq_active_module", "HRM");
    } catch {}

    window.location.href = `/matching-personeel?${params.toString()}`;
  }

  async function loadContacts(organisationId) {
    const id = safeTrim(organisationId);
    if (!id) {
      setContacts([]);
      return;
    }

    try {
      const data = await tryFetchJson([`/api/contacts?organisation_id=${encodeURIComponent(id)}`]);
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      setContacts([]);
    }
  }

  async function loadList(keepSelection = true) {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (safeTrim(q)) params.set("q", safeTrim(q));
      const data = await tryFetchJson([`/api/vacancies?${params.toString()}`, `/api/vacatures?${params.toString()}`]);
      const rows = Array.isArray(data) ? data.map(normalizeVacancy) : [];

      setItems(rows);

      if (keepSelection && selectedId) {
        const idx = rows.findIndex((x) => String(x.id) === String(selectedId));
        if (idx >= 0) {
          setListIndex(idx);
          return rows;
        }
      }

      setListIndex(rows.length ? 0 : -1);
      return rows;
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
      setListIndex(-1);
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrders();
    loadOrganisations();
    loadApplications();
  }, []);

  useEffect(() => {
    loadContacts(form.organisation_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.organisation_id]);

  useEffect(() => {
    loadList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const applicationId = safeTrim(getParam("application_id"));
    const createNew = safeTrim(getParam("new"));

    if (!applicationId || createNew !== "1" || createFromApplicationHandledRef.current) return;

    createFromApplicationHandledRef.current = true;
    setSelectedId(null);
    setForm({ ...emptyFormToday(), application_id: applicationId });
    setMode("form");
    setSaveError(null);

    setTimeout(async () => {
      await applyApplicationToForm(applicationId);

      try {
        window.history.replaceState({}, "", window.location.pathname);
      } catch {}

      try {
        rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        dateRef.current?.focus?.({ preventScroll: true });
      } catch {}
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedOrganisation = useMemo(() => {
    const oid = String(form.organisation_id || "");
    if (!oid) return null;
    return (organisations || []).find((o) => String(orgIdOf(o)) === oid) || null;
  }, [form.organisation_id, organisations]);

  const formCompanyCodeName =
    pickCompanyCodeName(selectedOrganisation) ||
    pickCompanyCodeName(form);

  function relationName(item) {
    return orgNameFromList(organisations, item?.organisation_id, item?.organisation_name || "");
  }

  function rowText(item) {
    return [
      item?.vacancy_date,
      item?.vacancy_number,
      relationName(item),
      item?.title,
      item?.job_title,
      item?.status,
      orderReference(item),
      item?.number_requested,
      item?.work_description,
      item?.start_date,
      item?.end_date,
      item?.notice_period,
      item?.hours_per_week,
      item?.schedule_text,
      item?.location,
      item?.location_contact,
      item?.registration_procedure,
      item?.place,
      item?.region,
      item?.education,
      item?.experience,
      item?.certificates,
      item?.driving_license,
      item?.own_transport,
      item?.languages,
      item?.matching_text,
      item?.cao,
      item?.wage_period,
      item?.invoice_period,
      item?.factor,
      item?.rate_indication,
      item?.sales_rate,
      item?.min_rate ?? item?.max_rate,
      item?.travel_costs,
      item?.travel_costs_unit,
      item?.travel_hours_amount,
      item?.allowances,
      item?.invoice_agreement,
      item?.priority,
      item?.request_source,
      item?.proposal_deadline,
      item?.internal_note,
      item?.external_note,
      item?.remarks,
      pickVacancyCompanyCodeName(item, organisations),
      boolToJaNee(parseActiveValue(item) !== false),
    ].join(" ");
  }

  function filterBySelect(value, filter) {
    if (!filter) return true;
    const v = safeTrim(value);
    if (filter === EMPTY_VAL) return !v;
    return norm(v) === norm(filter);
  }

  const filtered = useMemo(() => {
    const query = norm(q);

    return (items || []).filter((item) => {
      if (
        organisationFilterId &&
        String(item.organisation_id || "") !== String(organisationFilterId)
      ) {
        return false;
      }

      if (query && !norm(rowText(item)).includes(query)) return false;
      if (!filterBySelect(formatDateNL(item.vacancy_date), dateFilter)) return false;
      if (!filterBySelect(relationName(item), relationFilter)) return false;
      if (!filterBySelect(item.title, titleFilter)) return false;
      if (!filterBySelect(item.job_title, functionFilter)) return false;
      if (!filterBySelect(item.status, statusFilter)) return false;
      if (!filterBySelect(orderReference(item), orderFilter)) return false;
      if (!filterBySelect(item.cao, caoFilter)) return false;
      if (!filterBySelect(item.wage_period, wagePeriodFilter)) return false;
      if (!filterBySelect(item.invoice_period, invoicePeriodFilter)) return false;
      if (!filterBySelect(decimalInput(item.factor), factorFilter)) return false;
      if (!filterBySelect(pickVacancyCompanyCodeName(item, organisations), companyCodeFilter)) return false;

      const activeBool = parseActiveValue(item) !== false;
      if (activeFilter === "j" && !activeBool) return false;
      if (activeFilter === "n" && activeBool) return false;

      return true;
    });
  }, [items, q, dateFilter, relationFilter, titleFilter, functionFilter, statusFilter, orderFilter, caoFilter, wagePeriodFilter, invoicePeriodFilter, factorFilter, companyCodeFilter, activeFilter, organisations, organisationFilterId]);

  const sorted = useMemo(() => {
    const arr = filtered.slice();
    const dir = sort.dir === "desc" ? -1 : 1;

    arr.sort((a, b) => {
      function val(x) {
        if (sort.key === "date") return x.vacancy_date || "";
        if (sort.key === "relation") return relationName(x);
        if (sort.key === "title") return x.title || "";
        if (sort.key === "function") return x.job_title || "";
        if (sort.key === "status") return x.status || "";
        if (sort.key === "order") return orderReference(x);
        if (sort.key === "cao") return x.cao || "";
        if (sort.key === "wage") return x.wage_period || "";
        if (sort.key === "invoice") return x.invoice_period || "";
        if (sort.key === "factor") return Number(String(x.factor || "").replace(",", ".")) || 0;
        if (sort.key === "companyCode") return pickVacancyCompanyCodeName(x, organisations);
        if (sort.key === "active") return parseActiveValue(x) !== false ? "j" : "n";
        return "";
      }

      const av = val(a);
      const bv = val(b);

      if (typeof av === "number" || typeof bv === "number") {
        return ((av || 0) - (bv || 0)) * dir;
      }

      return String(av).localeCompare(String(bv), "nl", { numeric: true }) * dir;
    });

    return arr;
  }, [filtered, sort, organisations]);

  const gridCount = sorted.length > 9999 ? "9999+" : sorted.length;

  const dateOptions = uniqSorted(items.map((x) => formatDateNL(x.vacancy_date)));
  const relationOptions = uniqSorted(items.map((x) => relationName(x)));
  const titleOptions = uniqSorted(items.map((x) => x.title));
  const functionOptions = uniqSorted(items.map((x) => x.job_title));
  const statusOptions = uniqSorted(items.map((x) => x.status));
  const orderOptions = uniqSorted(items.map((x) => orderReference(x)));
  const caoOptions = uniqSorted(items.map((x) => x.cao));
  const wageOptions = uniqSorted(items.map((x) => x.wage_period));
  const invoiceOptions = uniqSorted(items.map((x) => x.invoice_period));
  const factorOptions = uniqSorted(items.map((x) => decimalInput(x.factor)));
  const companyCodeOptions = uniqSorted(items.map((x) => pickVacancyCompanyCodeName(x, organisations)));

  useEffect(() => {
    if (loading || reopenHandledRef.current || !items.length) return;

    let requestedId = "";
    try {
      requestedId = String(
        sessionStorage.getItem("staffiq_reopen_vacancy_id") || ""
      ).trim();
    } catch {}

    if (!requestedId) {
      reopenHandledRef.current = true;
      return;
    }

    const row = items.find(
      (item) => String(item.id) === String(requestedId)
    );

    if (!row) return;

    reopenHandledRef.current = true;

    try {
      sessionStorage.removeItem("staffiq_reopen_vacancy_id");
    } catch {}

    const visibleIndex = sorted.findIndex(
      (item) => String(item.id) === String(requestedId)
    );

    setListIndex(visibleIndex >= 0 ? visibleIndex : 0);
    loadItem(row);
  }, [loading, items, sorted]);

  async function loadItem(row) {
    if (!row?.id) return;

    try {
      setApplicationReadyMessage("");
      setSaveError(null);
      const data = await tryFetchJson([`/api/vacancies/${row.id}`, `/api/vacatures/${row.id}`]);
      const item = normalizeVacancy(data);
      setSelectedId(item.id);
      setForm({ ...emptyFormToday(), ...item });
      setMode("form");
      loadVacancyDocumentSettings(item.company_code_id).catch(() => {});

      requestAnimationFrame(() => {
        try {
          rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        } catch {}
      });
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function newItem() {
    if (isReadonlyRole) return;
    setApplicationReadyMessage("");
    setSelectedId(null);
    setForm(emptyFormToday());
    setMode("form");
    setSaveError(null);
    requestAnimationFrame(() => {
      try {
        rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        dateRef.current?.focus?.();
      } catch {}
    });
  }

  function vernieuwList() {
    setApplicationReadyMessage("");
    setMode("search");
    setOrganisationFilterId("");
    setSelectedId(null);
    setForm(emptyFormToday());
    setSaveError(null);
    loadList(false);
  }

  async function saveItem() {
    if (isReadonlyRole) return;

    const saveId = form.id || selectedId || null;

    const payload = {
      ...form,
      id: saveId,
      application_id: form.application_id ? Number(form.application_id) : null,
      order_id: form.order_id ? Number(form.order_id) : null,
      organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
      contact_id: form.contact_id ? Number(form.contact_id) : null,
      company_code_id: pickVacancyCompanyCodeId(form, organisations) ? Number(pickVacancyCompanyCodeId(form, organisations)) : null,
      number_requested: form.number_requested ? Number(form.number_requested) : null,
      number_proposed: form.number_proposed ? Number(form.number_proposed) : 0,
      number_placed: form.number_placed ? Number(form.number_placed) : 0,
      hours_per_week: form.hours_per_week === "" ? null : parseDecimal(form.hours_per_week),
      factor: form.factor === "" ? null : parseDecimal(form.factor),
      rate_indication: form.rate_indication === "" ? null : parseDecimal(form.rate_indication),
      sales_rate: form.sales_rate === "" ? null : parseDecimal(form.sales_rate),
      min_rate: form.min_rate === "" ? null : parseDecimal(form.min_rate),
      max_rate: form.min_rate === "" ? null : parseDecimal(form.min_rate),
      travel_costs: form.travel_costs === "" ? null : parseDecimal(form.travel_costs),
      travel_costs_unit: form.travel_costs_unit === "day" ? "day" : "km",
      travel_hours_amount: form.travel_hours_amount === "" ? null : parseDecimal(form.travel_hours_amount),
      plan_weekends: form.plan_weekends === true,
      company_transport: form.company_transport === true,
      employment_vast: form.employment_vast !== false,
      employment_temporary: form.employment_temporary !== false,
      employment_zzp: form.employment_zzp !== false,
      employment_doorleen: form.employment_doorleen !== false,
      active: form.active !== false,
    };

    try {
      setSaveError(null);

      const saved = await tryFetchJson(
        saveId
          ? [`/api/vacancies/${saveId}`, `/api/vacatures/${saveId}`]
          : ["/api/vacancies", "/api/vacatures"],
        {
          method: saveId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const normalized = normalizeVacancy(saved || payload);
      setSelectedId(normalized.id);
      setForm({ ...emptyFormToday(), ...normalized });
      setItems((prev) => {
        const idx = prev.findIndex((x) => String(x.id) === String(normalized.id));
        if (idx === -1) return [normalized, ...prev];
        const copy = prev.slice();
        copy[idx] = normalized;
        return copy;
      });

      setApplicationReadyMessage("");
      setMode("search");
      setFlash("Vacature opgeslagen ✅");
      setTimeout(() => setFlash(""), 1000);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonlyRole || !form.id) return;
    if (!window.confirm("Vacature verwijderen?")) return;

    try {
      await tryFetchJson([`/api/vacancies/${form.id}`, `/api/vacatures/${form.id}`], { method: "DELETE" });
      setItems((prev) => prev.filter((x) => String(x.id) !== String(form.id)));
      setApplicationReadyMessage("");
      setSelectedId(null);
      setForm(emptyFormToday());
      setMode("search");
      setFlash("Vacature verwijderd ✅");
      setTimeout(() => setFlash(""), 1000);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function moveCursor(delta) {
    if (!sorted.length) return;
    let idx = listIndex;
    if (idx < 0) idx = 0;
    idx = Math.max(0, Math.min(sorted.length - 1, idx + delta));
    setListIndex(idx);

    requestAnimationFrame(() => {
      try {
        rowRefs.current[idx]?.scrollIntoView?.({ block: "nearest" });
      } catch {}
    });
  }

  function openCurrentRow() {
    const idx = listIndex >= 0 ? listIndex : 0;
    const row = sorted[idx];
    if (row) loadItem(row);
  }

  function getFocusableElements() {
    return focusOrderRefs
      .map((r) => r?.current)
      .filter(Boolean)
      .filter((el) => {
        try {
          if (el.disabled) return false;
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        } catch {
          return false;
        }
      });
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : 0;

    const next = els[Math.max(0, Math.min(els.length - 1, idx + dir))];
    try {
      next?.focus?.();
      next?.select?.();
    } catch {}
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 5000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;

      if (cmd.type === "next") {
        if (mode === "form") focusNextField(+1);
        else moveCursor(+1);
        return true;
      }

      if (cmd.type === "prev") {
        if (mode === "form") focusNextField(-1);
        else moveCursor(-1);
        return true;
      }

      if (cmd.type === "new") return newItem(), true;
      if (cmd.type === "refresh") return vernieuwList(), true;
      if (cmd.type === "save") return saveItem(), true;
      if (cmd.type === "delete") return deleteItem(), true;
      if (cmd.type === "select") return openCurrentRow(), true;

      return false;
    },
    onDictation: (text) => {
      const raw = String(text || "").trim();
      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw);
      if (t === "volgende") return mode === "form" ? focusNextField(+1) : moveCursor(+1);
      if (t === "vorige" || t === "terug") return mode === "form" ? focusNextField(-1) : moveCursor(-1);
      if (t === "nieuw") return newItem();
      if (t === "vernieuw" || t === "vernieuwen") return vernieuwList();
      if (t === "opslaan") return saveItem();
      if (t === "selecteer" || t === "item" || t === "rechts") return openCurrentRow();
      if (t === "lijst" || t === "links") return setMode("search");

      if (mode === "search") {
        if (isEmptyWord(raw)) setQ("");
        else setQ(raw);
      }
    },
  });

  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 1365;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

  const colWidths = {
    date: 90,
    relation: 150,
    title: 125,
    function: 115,
    status: 75,
    order: 95,
    cao: 75,
    wage: 90,
    invoice: 100,
    factor: 65,
    companyCode: 90,
    active: 55,
  };

  const searchInputStyle = {
    padding: "7px 10px",
    width: isMobile ? "100%" : 220,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    display: "block",
    padding: "7px 10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";
  const labelStyle = { fontSize: 13, color: "#333" };
  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    opacity: 0.75,
    marginTop: 8,
  };

  function filterSelect(value, setValue, options, includeYesNo = false) {
    return (
      <select value={value} onChange={(e) => setValue(e.target.value)} style={selectStyle}>
        <option value="">(alle)</option>
        {includeYesNo ? (
          <>
            <option value="j">j</option>
            <option value="n">n</option>
          </>
        ) : (
          options.map((x) => (
            <option key={x || EMPTY_VAL} value={x || EMPTY_VAL}>
              {x || "(leeg)"}
            </option>
          ))
        )}
      </select>
    );
  }

  function sortToggle(key) {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === "date" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function renderTextField(label, key, ref = null, type = "text", extra = {}) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type={type}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => {
            if (extra.onChange) {
              extra.onChange(e.target.value);
              return;
            }
            setForm({ ...form, [key]: e.target.value });
          }}
          style={inputStyle}
          placeholder={extra.placeholder || ""}
          list={extra.list || undefined}
        />
      </>
    );
  }

  function renderMoneyField(label, key, ref = null, decimals = 2) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => {
            const clean = cleanDecimalInputValue(e.target.value);
            setForm({ ...form, [key]: clean });
          }}
          onBlur={() => {
            const raw = String(form[key] || "").trim();
            if (!raw) return;
            const n = parseDecimal(raw);
            if (!Number.isFinite(n)) return;
            setForm((prev) => ({ ...prev, [key]: formatDecimalInputValue(prev[key], decimals) }));
          }}
          style={inputStyle}
          placeholder="0,00"
        />
      </>
    );
  }

  function renderDecimalField(label, key, ref = null, decimals = 2) {
    return renderMoneyField(label, key, ref, decimals);
  }

  function renderSelectField(label, key, ref, options) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <select
          ref={ref}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          style={selectStyle}
        >
          <option value="">-</option>
          {options.map((x) => (
            <option key={x} value={x}>{x}</option>
          ))}
        </select>
      </>
    );
  }

  function renderTextareaField(label, key, ref = null, minHeight = 70) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <textarea
          ref={ref}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          style={{ ...inputStyle, minHeight, resize: "vertical" }}
        />
      </>
    );
  }

  function renderCheckbox(label, key) {
    return (
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
        <input
          type="checkbox"
          checked={form[key] === true}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
        />
        {label}
      </label>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <h1 ref={pageTopRef} style={{ marginTop: 0, marginBottom: 0 }}>
              Vacatures <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
            </h1>
            <HelpButton topic="vacatures" title="Help over Vacatures" />
          </div>

          {!isMobile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setMode("search");
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => voice.startOnce?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>
                ➕ Nieuw
              </button>

              <button onClick={vernieuwList} style={buttonStyle}>
                🔄 Vernieuw
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 58px", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setMode("search");
                  }}
                  placeholder="Zoek..."
                  style={{ ...searchInputStyle, width: "100%", minWidth: 0 }}
                />
                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                <button onClick={() => voice.startOnce?.()} disabled={!voice.supported || voice.listening} style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}>
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>
                <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>➕ Nieuw</button>
                <button onClick={vernieuwList} style={buttonStyle}>🔄 Vernieuw</button>
              </div>
            </>
          )}

          {speechInfo && <div style={{ marginTop: 8, fontSize: 13, color: "#1f4e79" }}>{speechInfo}</div>}
          {applicationReadyMessage && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 10px",
                border: "1px solid #9fc5e8",
                borderRadius: 5,
                background: "#eef7ff",
                color: "#17496b",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {applicationReadyMessage}
            </div>
          )}
          {flash && <div style={{ marginTop: 8, fontSize: 13, color: "#1f7a1f" }}>{flash}</div>}
          {error && <div style={{ marginTop: 8, color: "#b00020" }}>{error}</div>}

          <div
            style={{
              marginTop: 12,
              border: "1px solid #e5e5e5",
              borderRadius: 6,
              background: "white",
              overflow: "auto",
              maxHeight: isMobile ? "45vh" : `calc(100dvh - ${MID_BOTTOM_OFFSET}px)`,
            }}
          >
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: TABLE_MIN_WIDTH, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: colWidths.date }} />
                <col style={{ width: colWidths.relation }} />
                <col style={{ width: colWidths.title }} />
                <col style={{ width: colWidths.function }} />
                <col style={{ width: colWidths.status }} />
                <col style={{ width: colWidths.order }} />
                <col style={{ width: colWidths.cao }} />
                <col style={{ width: colWidths.wage }} />
                <col style={{ width: colWidths.invoice }} />
                <col style={{ width: colWidths.factor }} />
                <col style={{ width: colWidths.active }} />
                <col style={{ width: colWidths.companyCode }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thClickable} onClick={() => sortToggle("date")}>Vacaturedatum{sortArrow("date")}</th>
                  <th style={thClickable} onClick={() => sortToggle("relation")}>Relatie{sortArrow("relation")}</th>
                  <th style={thClickable} onClick={() => sortToggle("title")}>Korte omschrijving{sortArrow("title")}</th>
                  <th style={thClickable} onClick={() => sortToggle("function")}>Functie{sortArrow("function")}</th>
                  <th style={thClickable} onClick={() => sortToggle("status")}>Status{sortArrow("status")}</th>
                  <th style={thClickable} onClick={() => sortToggle("order")}>Opdracht{sortArrow("order")}</th>
                  <th style={thClickable} onClick={() => sortToggle("cao")}>CAO{sortArrow("cao")}</th>
                  <th style={thClickable} onClick={() => sortToggle("wage")}>Loonperiode{sortArrow("wage")}</th>
                  <th style={thClickable} onClick={() => sortToggle("invoice")}>Factuurperiode{sortArrow("invoice")}</th>
                  <th style={thClickable} onClick={() => sortToggle("factor")}>Factor{sortArrow("factor")}</th>
                  <th style={thClickable} onClick={() => sortToggle("active")}>Actief{sortArrow("active")}</th>
                  <th style={thClickable} onClick={() => sortToggle("companyCode")}>Bedrijfscode{sortArrow("companyCode")}</th>
                </tr>
                <tr>
                  <th style={filterThStyle}>{filterSelect(dateFilter, setDateFilter, dateOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(relationFilter, setRelationFilter, relationOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(titleFilter, setTitleFilter, titleOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(functionFilter, setFunctionFilter, functionOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(statusFilter, setStatusFilter, statusOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(orderFilter, setOrderFilter, orderOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(caoFilter, setCaoFilter, caoOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(wagePeriodFilter, setWagePeriodFilter, wageOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(invoicePeriodFilter, setInvoicePeriodFilter, invoiceOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(factorFilter, setFactorFilter, factorOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(activeFilter, setActiveFilter, [], true)}</th>
                  <th style={filterThStyle}>{filterSelect(companyCodeFilter, setCompanyCodeFilter, companyCodeOptions)}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} style={tdStyle}>Laden...</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={12} style={tdStyle}>Geen vacatures gevonden.</td></tr>
                ) : (
                  sorted.map((item, idx) => {
                    const isSelected = String(item.id) === String(selectedId);
                    const isCursor = idx === listIndex;
                    return (
                      <tr
                        key={item.id || idx}
                        ref={(el) => (rowRefs.current[idx] = el)}
                        onClick={() => {
                          setListIndex(idx);
                          loadItem(item);
                        }}
                        style={{
                          background: isSelected ? rowBgSelected : isCursor ? rowBgCursor : "white",
                          cursor: "pointer",
                        }}
                      >
                        <td style={tdStyle}>{formatDateNL(item.vacancy_date)}</td>
                        <td style={tdStyle} title={relationName(item)}>{relationName(item)}</td>
                        <td style={tdStyle} title={item.title}>{item.title}</td>
                        <td style={tdStyle} title={item.job_title}>{item.job_title}</td>
                        <td style={tdStyle}>{item.status}</td>
                        <td style={tdStyle} title={orderReference(item)}>{orderReference(item)}</td>
                        <td style={tdStyle}>{item.cao}</td>
                        <td style={tdStyle}>{item.wage_period}</td>
                        <td style={tdStyle}>{item.invoice_period}</td>
                        <td style={tdStyle}>{decimalInput(item.factor)}</td>
                        <td style={tdStyle}>{boolToJaNee(parseActiveValue(item) !== false)}</td>
                        <td style={tdStyle} title={pickVacancyCompanyCodeName(item, organisations)}>{pickVacancyCompanyCodeName(item, organisations)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            paddingBottom: isMobile ? 34 : 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            marginBottom: isMobile ? 34 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 12,
              paddingBottom: isMobile ? 28 : 12,
              boxSizing: "border-box",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: isMobile ? "visible" : "hidden",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Vacature</h2>
              <span style={{ fontSize: 12, opacity: 0.55 }}>{mode === "form" ? (form.id ? `#${form.id}` : "nieuw") : "geen selectie"}</span>
            </div>

            {saveError && <div style={{ marginBottom: 10, color: "#b00020", fontSize: 13 }}>{saveError}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              <div style={sectionTitleStyle}>Algemeen</div>
              {renderTextField("Vacaturedatum", "vacancy_date", dateRef, "date")}
              {renderTextField("Vacaturereferentie", "vacancy_number", numberRef)}

              <label style={labelStyle}>Aanvraag</label>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 6 }}>
                <select
                  ref={applicationRef}
                  value={form.application_id || ""}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, application_id: e.target.value })}
                  style={selectStyle}
                >
                  <option value="">-</option>
                  {applications.map((a) => (
                    <option key={a.id} value={a.id}>{applicationLabel(a)}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => applyApplicationToForm()}
                  disabled={isFormLocked || !form.application_id}
                  style={isFormLocked || !form.application_id ? buttonDisabledStyle : buttonStyle}
                  title="Gegevens uit de gekozen aanvraag overnemen"
                >
                  Overnemen
                </button>
              </div>

              <label style={labelStyle}>Opdracht</label>
              <select
                ref={orderRef}
                value={form.order_id || ""}
                disabled={isFormLocked}
                onChange={(e) => applyOrderDefaults(e.target.value)}
                style={selectStyle}
              >
                <option value="">-</option>
                {(orders || []).filter((o) => o?.active !== false).map((o) => {
                  const label = [safeTrim(o?.order_reference), safeTrim(o?.organisation_name)].filter(Boolean).join(" - ") || `Opdracht #${o?.id}`;
                  return <option key={o.id} value={o.id}>{label}</option>;
                })}
              </select>

              <label style={labelStyle}>Relatie</label>
              <select
                ref={relationRef}
                value={form.organisation_id || ""}
                disabled={isFormLocked}
                onChange={(e) => {
                  const organisationId = e.target.value;
                  const org = (organisations || []).find((o) => String(orgIdOf(o)) === String(organisationId));
                  setForm((prev) => ({
                    ...prev,
                    organisation_id: organisationId,
                    contact_id: "",
                    company_code_id: companyCodeIdFromOrganisation(organisations, organisationId, ""),
                    company_code_name: companyCodeNameFromOrganisation(organisations, organisationId, ""),
                    location: safeTrim(prev.location) || organisationVisitAddressText(org),
                    place: safeTrim(prev.place) || organisationVisitPlace(org),
                    region: safeTrim(prev.region) || organisationVisitRegion(org),
                  }));
                }}
                style={selectStyle}
              >
                <option value="">-</option>
                {organisations.map((o) => {
                  const id = orgIdOf(o);
                  const name = pickOrgName(o);
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>

              <label style={labelStyle}>Contactpersoon</label>
              <select
                ref={contactRef}
                value={form.contact_id || ""}
                disabled={isFormLocked || !form.organisation_id}
                onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                style={selectStyle}
              >
                <option value="">-</option>
                {contacts.map((c) => {
                  const id = contactIdOf(c);
                  const name = pickContactName(c);
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>

              <label style={labelStyle}>Bedrijfscode</label>
              <input
                value={formCompanyCodeName || ""}
                disabled
                readOnly
                style={{ ...inputStyle, background: "#f7fafc" }}
              />

              {renderTextField("Functie / vacaturetitel", "job_title", functionRef)}
              {renderTextField("Korte omschrijving", "title", titleRef)}
              {renderTextField("Aantal gevraagd", "number_requested", peopleRef, "number")}
              {renderTextField("Aantal voorgesteld", "number_proposed", null, "number")}
              {renderTextField("Aantal geplaatst", "number_placed", null, "number")}
              {renderSelectField("Status", "status", statusRef, ["Concept", "Open", "In behandeling", "Kandidaten voorgesteld", "Gedeeltelijk ingevuld", "Ingevuld", "On hold", "Vervallen", "Geannuleerd"])}
              {renderTextareaField("Interne omschrijving", "internal_description", null, 80)}
              {renderTextareaField("Externe vacaturetekst", "external_text", null, 90)}
              {renderTextareaField("Omschrijving werkzaamheden", "work_description", workDescriptionRef, 80)}
              {renderTextareaField("Functie-eisen", "requirements_text", null, 80)}
              {renderTextareaField("Aanbod / voorwaarden", "offer_text", null, 70)}

              <div style={sectionTitleStyle}>Periode / locatie</div>
              {renderTextField("Startdatum", "start_date", startDateRef, "date")}
              {renderTextField("Einddatum", "end_date", endDateRef, "date")}
              {renderTextField("Opzegtermijn", "notice_period", noticePeriodRef)}
              {renderDecimalField("Uren per week", "hours_per_week", hoursRef, 2)}
              {renderCheckbox("Doorplannen in weekend", "plan_weekends")}
              {renderTextareaField("Werktijden / roostertekst", "schedule_text", scheduleRef, 70)}
              {renderTextareaField("Locatie / bezoekadres", "location", locationRef, 96)}
              {renderTextField("Contact op locatie", "location_contact", locationContactRef)}
              {renderTextareaField("Aanmeldprocedure", "registration_procedure", registrationProcedureRef, 70)}
              {renderTextField("Regio", "region", regionRef)}

              <div style={sectionTitleStyle}>Eisen / profiel</div>
              {renderTextField("Opleiding", "education", educationRef)}
              {renderTextField("Werkervaring", "experience", experienceRef)}
              {renderTextField("Certificaten", "certificates", certificatesRef)}
              {renderTextField("Rijbewijs", "driving_license", drivingLicenseRef)}
              {renderSelectField("Eigen vervoer", "own_transport", ownTransportRef, ["Ja", "Nee", "Gewenst", "Niet nodig"])}
              {renderCheckbox("Vervoer van de zaak mogelijk", "company_transport")}

              <div style={sectionTitleStyle}>Toegestane inzetvorm</div>
              {renderCheckbox("Vast", "employment_vast")}
              {renderCheckbox("Tijdelijk", "employment_temporary")}
              {renderCheckbox("ZZP", "employment_zzp")}
              {renderCheckbox("Doorleen", "employment_doorleen")}

              {renderTextField("Talen", "languages", languagesRef)}
              {renderTextareaField("Matchingtekst / profiel", "matching_text", matchingTextRef, 90)}

              <div style={sectionTitleStyle}>Tarieven / afspraken</div>
              {renderTextField("CAO", "cao", caoRef)}
              {renderSelectField("Loonperiode", "wage_period", wagePeriodRef, ["week", "4-weken", "maand"])}
              {renderSelectField("Factuurperiode", "invoice_period", invoicePeriodRef, ["week", "4-weken", "maand"])}
              {renderDecimalField("Factor", "factor", factorRef, 2)}
              {renderMoneyField("Salarisindicatie", "rate_indication", rateIndicationRef)}
              {renderMoneyField("Verkooptarief", "sales_rate", salesRateRef)}
              {renderMoneyField("Minimaal tarief", "min_rate", minRateRef)}
              <label style={labelStyle}>Reiskosten</label>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 90px", gap: 6 }}>
                <input
                  ref={travelCostsRef}
                  type="text"
                  inputMode="decimal"
                  value={form.travel_costs || ""}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, travel_costs: cleanDecimalInputValue(e.target.value) })}
                  onBlur={() => {
                    const raw = String(form.travel_costs || "").trim();
                    if (!raw) return;
                    setForm((prev) => ({ ...prev, travel_costs: formatDecimalInputValue(prev.travel_costs, 2) }));
                  }}
                  style={inputStyle}
                  placeholder="0,00"
                />
                <select
                  ref={travelCostsUnitRef}
                  value={form.travel_costs_unit || "km"}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, travel_costs_unit: e.target.value })}
                  style={selectStyle}
                >
                  <option value="km">per km</option>
                  <option value="day">per dag</option>
                </select>
              </div>
              {renderMoneyField("Reisuren per dag", "travel_hours_amount", travelHoursAmountRef, 2)}
              {renderTextareaField("Toeslagen", "allowances", allowancesRef, 60)}
              {renderTextareaField("Factuurafspraak", "invoice_agreement", invoiceAgreementRef, 70)}

              <div style={sectionTitleStyle}>Proces</div>
              {renderSelectField("Prioriteit", "priority", priorityRef, ["Laag", "Normaal", "Hoog", "Spoed"])}
              {renderTextField("Bron aanvraag", "request_source", sourceRef)}
              {renderTextField("Deadline voorstellen", "proposal_deadline", proposalDeadlineRef, "date")}

              <div style={sectionTitleStyle}>Opmerkingen</div>
              {renderTextareaField("Opmerking intern", "internal_note", internalNoteRef, 80)}
              {renderTextareaField("Opmerking extern", "external_note", externalNoteRef, 80)}
              {renderCheckbox("Actief", "active")}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginTop:8}}>
                <button type="button" onClick={()=>vacancyDocumentAction("preview")} disabled={!form.id||documentBusy} style={!form.id||documentBusy?buttonDisabledStyle:buttonStyle}>👁 Voorbeeld</button>
                <button type="button" onClick={()=>vacancyDocumentAction("save")} disabled={!form.id||documentBusy} style={!form.id||documentBusy?buttonDisabledStyle:buttonStyle}>💾 PDF opslaan</button>
                <button type="button" onClick={()=>vacancyDocumentAction("email")} disabled={!form.id||documentBusy} style={!form.id||documentBusy?buttonDisabledStyle:buttonStyle}>✉ E-mailen</button>
                <button type="button" onClick={()=>vacancyDocumentAction("whatsapp")} disabled={!form.id||documentBusy} style={!form.id||documentBusy?buttonDisabledStyle:buttonStyle}>💬 WhatsApp</button>
              </div>

              <button
                type="button"
                onClick={openMatchingPersoneel}
                disabled={!form.id && !selectedId}
                style={{
                  ...(!form.id && !selectedId
                    ? { ...buttonPrimary("#198754"), opacity: 0.5, cursor: "not-allowed" }
                    : buttonPrimary("#198754")),
                  width: "100%",
                  minWidth: 0,
                  height: 32,
                  padding: "7px 8px",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                  marginTop: 8,
                }}
              >
                🎯 Match personeel
              </button>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginTop: 8,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <button
                  onClick={saveItem}
                  disabled={isReadonlyRole || mode !== "form"}
                  style={{
                    ...(isReadonlyRole || mode !== "form"
                      ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#2f7dd1")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  💾 Opslaan
                </button>

                <button
                  onClick={deleteItem}
                  disabled={isReadonlyRole || !form.id}
                  style={{
                    ...(isReadonlyRole || !form.id
                      ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#d64545")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  🗑 Verwijderen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
